import { ReactNode, useState } from "react";
import useTranslation from "../../hooks/useTranslation";
import { Activity } from "../../models/activity";
import { ActionPlanTimeline } from "../ActionPlanTimeline/ActionPlanTimeline";
import { Snackbar } from "../ui/Snackbar/Snackbar";
import { Typography } from "../ui/Typography/Typography";

interface ActionPlanOverviewProps {
  title: string;
  description?: string;
  activities: Activity[];
  hasSurveyAccess?: boolean;
  setActivities: (activities: Activity[]) => void;
  onActivitiesAdd: (activities: Activity[]) => void;
  renderButtons: () => ReactNode;
}

export const ActionPlanOverviewStep = ({
  title,
  description,
  activities,
  hasSurveyAccess,
  setActivities,
  onActivitiesAdd,
  renderButtons,
}: ActionPlanOverviewProps) => {
  const { t } = useTranslation("actionPlanPage");
  const [snackBarOpen, setSnackBarOpen] = useState(false);

  const handleActivityRemove = (activity: Activity) => {
    const updatedActivities = activities.filter(
      (a) => a.clientId !== activity.clientId
    );
    setActivities(updatedActivities);
    setSnackBarOpen(true);
  };

  const handleActivityUpdate = (activity: Activity) => {
    const updatedActivities = activities.map((a) =>
      a.clientId === activity.clientId ? activity : a
    );
    setActivities(updatedActivities);
    setSnackBarOpen(true);
  };

  const handleActivitiesAdd = (newActivities: Activity[]) => {
    onActivitiesAdd(newActivities);
    setSnackBarOpen(true);
  };

  return (
    <div className="ActionPlanOverview">
      <Typography desktop="h1" color="error">
        {title}
      </Typography>
      {description && (
        <Typography
          desktop="body1"
          className="ActionPlanOverview__info"
          color="secondary"
        >
          {description}
        </Typography>
      )}

      <ActionPlanTimeline
        activities={activities}
        now={new Date()}
        rangeLength={12}
        isSettingUp={true}
        hasSurveyAccess={hasSurveyAccess}
        onActivitiesAdd={handleActivitiesAdd}
        onActivityRemove={handleActivityRemove}
        onActivityUpdate={handleActivityUpdate}
      />

      {renderButtons()}

      <Snackbar
        open={snackBarOpen}
        onClose={() => setSnackBarOpen(false)}
        message={t("feedback.success")}
        autoHideDuration={3000}
      />
    </div>
  );
};
